import { useState, useEffect, useCallback } from "react";
import type { AdminVoucher, WorkshopKind, WorkshopKindID } from "../../lib/types";
import { adminListVouchers, adminCreateVoucher, adminVoidVoucher, adminDownloadVoucherPDF, getWorkshopKinds, toLocalDateStr } from "../../lib/hooks";

type StatusFilter = "" | "active" | "redeemed" | "void";

const STATUS_LABELS: Record<AdminVoucher["status"], string> = {
  active: "Aktívny",
  redeemed: "Uplatnený",
  void: "Zneplatnený",
};

const SOURCE_LABELS: Record<AdminVoucher["source"], string> = {
  online: "Online",
  offline: "V štúdiu",
};

// Same rule as the backend (voucher.Validate): capitals, digits, hyphens, 6–32 chars.
const CODE_PATTERN = /^[A-Z0-9-]{6,32}$/;

function formatEuro(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",") + " €";
}

function formatDate(iso: string): string {
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

/** Default expiry for a voucher sold in the studio: one year from today. */
function oneYearFromToday(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return toLocalDateStr(d);
}

export function ManageVouchers() {
  const [vouchers, setVouchers] = useState<AdminVoucher[]>([]);
  const [filter, setFilter] = useState<StatusFilter>("");
  const [loading, setLoading] = useState(true);

  // Create form
  const [kinds, setKinds] = useState<WorkshopKind[]>([]);
  const [type, setType] = useState<AdminVoucher["type"]>("credit");
  const [kind, setKind] = useState<WorkshopKindID | "">("");
  const [code, setCode] = useState("");
  const [value, setValue] = useState("");
  const [expiresAt, setExpiresAt] = useState(oneYearFromToday);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [created, setCreated] = useState<AdminVoucher | null>(null);

  // Void confirmation
  const [voidingId, setVoidingId] = useState<number | null>(null);
  const [voidError, setVoidError] = useState("");
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const handleDownload = async (v: AdminVoucher) => {
    setDownloadingId(v.id);
    setVoidError("");
    const { error } = await adminDownloadVoucherPDF(v.id, v.code);
    setDownloadingId(null);
    if (error) setVoidError(`Chyba: ${error.message}`);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await adminListVouchers(filter || undefined);
    setVouchers(data ?? []);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getWorkshopKinds().then(({ data }) => setKinds(data ?? []));
  }, []);

  const codeInvalid = code !== "" && !CODE_PATTERN.test(code);

  // Switching to a workshop voucher: the value follows the kind's list price (still editable).
  const selectType = (t: AdminVoucher["type"]) => {
    setType(t);
    setFormError("");
    if (t === "credit") {
      setKind("");
      setValue("");
    }
  };
  const selectKind = (id: WorkshopKindID | "") => {
    setKind(id);
    const k = kinds.find((x) => x.id === id);
    if (k) setValue(String(k.default_price_cents / 100));
  };

  const handleCreate = async () => {
    if (type === "workshop" && !kind) {
      setFormError("Vyberte druh workshopu");
      return;
    }
    const euros = parseFloat(value.replace(",", "."));
    if (isNaN(euros) || euros <= 0) {
      setFormError("Zadajte platnú hodnotu v €");
      return;
    }
    if (codeInvalid) {
      setFormError("Kód môže obsahovať len veľké písmená A–Z, číslice a pomlčky, 6 až 32 znakov");
      return;
    }
    setSaving(true);
    setFormError("");
    setCreated(null);
    const { data, error } = await adminCreateVoucher({
      code,
      type,
      workshop_kind: type === "workshop" && kind ? kind : undefined,
      value_cents: Math.round(euros * 100),
      expires_at: expiresAt,
      note: note.trim(),
    });
    setSaving(false);
    if (error || !data) {
      setFormError(error?.message === "Voucher code already exists" ? "Tento kód už existuje" : `Chyba: ${error?.message}`);
      return;
    }
    setCreated(data);
    setCode("");
    setValue(type === "workshop" && kind ? value : "");
    setExpiresAt(oneYearFromToday());
    setNote("");
    load();
  };

  const handleVoid = async (id: number) => {
    setVoidError("");
    const { error } = await adminVoidVoucher(id);
    setVoidingId(null);
    if (error) {
      setVoidError(`Chyba: ${error.message}`);
      return;
    }
    load();
  };

  const renderRow = (v: AdminVoucher) => (
    <div key={v.id} className={`admin-voucher-item status-${v.status}`}>
      <div className="admin-voucher-main">
        <span className="admin-voucher-code">{v.code}</span>
        <span className="admin-voucher-value">
          {v.type === "workshop" ? `Workshop ${v.workshop_kind_label ?? v.workshop_kind}` : formatEuro(v.value_cents)}
        </span>
        <span className={`admin-voucher-status status-${v.status}`}>{STATUS_LABELS[v.status]}</span>
        <span className="admin-voucher-source">{SOURCE_LABELS[v.source]}</span>
      </div>
      <div className="admin-voucher-meta">
        <span>Vytvorený {formatDate(v.created_at)}</span>
        {v.expires_at && <span>Platí do {formatDate(v.expires_at)}</span>}
        {v.buyer_email && <span>Kupujúci: {v.buyer_email}</span>}
        {v.note && <span>{v.note}</span>}
        {v.status === "redeemed" && (
          <span>
            Uplatnil{v.redeemed_by ? ` ${v.redeemed_by.name} (${v.redeemed_by.customer_code})` : ""}
            {v.redeemed_at ? ` ${formatDate(v.redeemed_at)}` : ""}
          </span>
        )}
        {v.status === "void" && v.voided_at && <span>Zneplatnený {formatDate(v.voided_at)}</span>}
      </div>
      {(v.status === "active" || v.pdf_available) && (
        <div className="admin-voucher-actions">
          {v.pdf_available && (
            <button className="btn btn-secondary btn-small" onClick={() => handleDownload(v)} disabled={downloadingId === v.id}>
              {downloadingId === v.id ? "Pripravujem..." : "Stiahnuť PDF"}
            </button>
          )}
          {v.status === "active" && (voidingId === v.id ? (
            <>
              <span className="admin-voucher-confirm">Naozaj zneplatniť {v.code}?</span>
              <button className="btn btn-danger btn-small" onClick={() => handleVoid(v.id)}>Áno, zneplatniť</button>
              <button className="btn btn-secondary btn-small" onClick={() => setVoidingId(null)}>Nie</button>
            </>
          ) : (
            <button className="btn btn-secondary btn-small" onClick={() => setVoidingId(v.id)}>Zneplatniť</button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-vouchers">
      <h2>Darčekové kupóny</h2>

      <div className="admin-card">
        <h3>Nový kupón (predaj v štúdiu)</h3>
        <div className="form-row">
          <div className="form-field">
            <label>Typ:</label>
            <div className="resource-picker">
              <button type="button" className={`resource-pick-btn ${type === "credit" ? "active" : ""}`} onClick={() => selectType("credit")}>
                Kredit
              </button>
              <button type="button" className={`resource-pick-btn ${type === "workshop" ? "active" : ""}`} onClick={() => selectType("workshop")}>
                Workshop
              </button>
            </div>
            <span className="form-hint">
              {type === "credit"
                ? "Hodnota sa obdarovanému pripíše ako kredit."
                : "Jedno miesto na ľubovoľnom budúcom workshope daného druhu; termín si obdarovaný vyberie pri uplatnení."}
            </span>
          </div>
          {type === "workshop" && (
            <div className="form-field">
              <label>Druh workshopu:</label>
              <select value={kind} onChange={(e) => selectKind(e.target.value as WorkshopKindID | "")}>
                <option value="">— vyberte druh —</option>
                {kinds.map((k) => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Kód:</label>
            <input
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setFormError(""); }}
              placeholder="prázdne = vygenerovať"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={32}
              className={codeInvalid ? "input-invalid" : ""}
            />
            <span className="form-hint">Len veľké písmená A–Z, číslice a pomlčky, 6–32 znakov. Presne tak, ako je napísaný na poukaze.</span>
          </div>
          <div className="form-field">
            <label>{type === "workshop" ? "Cena (€, pre evidenciu):" : "Hodnota (€):"}</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="napr. 50"
            />
            {type === "workshop" && <span className="form-hint">Predvyplnené cenníkom druhu. Poukaz platí na jedno miesto bez ohľadu na cenu termínu.</span>}
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label>Platí do:</label>
            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            <span className="form-hint">Predvyplnené na rok. Prázdne = bez expirácie.</span>
          </div>
          <div className="form-field">
            <label>Poznámka (voliteľná):</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="napr. predané 12. 10. p. Nováková, hotovosť"
            />
          </div>
        </div>
        {formError && <div className="admin-result warning">{formError}</div>}
        {created && (
          <div className="admin-result success">
            Kupón vytvorený: <strong className="admin-voucher-code">{created.code}</strong>
            {created.type === "workshop" ? ` na workshop ${created.workshop_kind_label}` : ` v hodnote ${formatEuro(created.value_cents)}`}
            {created.expires_at ? `, platí do ${formatDate(created.expires_at)}` : ""}.
          </div>
        )}
        <button className="btn btn-primary" onClick={handleCreate} disabled={saving || !value}>
          {saving ? "Ukladám..." : "Vytvoriť kupón"}
        </button>
      </div>

      <div className="sub-tab-nav">
        {([["", "Všetky"], ["active", "Aktívne"], ["redeemed", "Uplatnené"], ["void", "Zneplatnené"]] as [StatusFilter, string][]).map(([key, label]) => (
          <button key={key} className={`sub-tab-btn ${filter === key ? "active" : ""}`} onClick={() => setFilter(key)}>
            {label}
          </button>
        ))}
      </div>

      {voidError && <div className="admin-result warning">{voidError}</div>}
      {loading ? (
        <div className="loading-small">Načítavam...</div>
      ) : vouchers.length === 0 ? (
        <p className="empty">Žiadne kupóny.</p>
      ) : (
        <div className="admin-voucher-list">{vouchers.map(renderRow)}</div>
      )}
    </div>
  );
}
